import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { AlertCircle, Sun, Moon } from "lucide-react";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { clearPendingInviteCode, postJoinPath } from "../utils/postJoinRoute";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinLeaguePage() {
  const { inviteCode } = useParams();
  const nav = useNavigate();
  const { user, isLoading } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const [teamName, setTeamName] = useState("");

  useEffect(() => {
    if (!isLoading && !user) {
      sessionStorage.setItem("pendingInviteCode", inviteCode);
      nav("/signup");
    }
  }, [user, isLoading, inviteCode]);

  const joinLeague = async () => {
    setJoining(true); setError("");
    try {
      const { data } = await axios.post(
        `${API}/leagues/join-by-code/${inviteCode}`,
        { team_name: teamName || undefined },
        { headers: authHeaders() }
      );
      clearPendingInviteCode();
      // [P0] Land in the lobby, not the dashboard - the invite was to a draft.
      let league = null;
      try {
        const res = await axios.get(`${API}/leagues/${data.league_id}`, { headers: authHeaders() });
        league = res.data;
      } catch { league = { id: data.league_id }; }
      nav(postJoinPath(league));
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not join league");
    } finally {
      setJoining(false);
    }
  };

  const bg    = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const text  = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[rgba(0,0,0,0.50)]";
  const inputCls = isDark
    ? "bg-[#111111] border-[#252525] focus:border-[#FF6B00] text-[#F5F5F5] placeholder-[#A0A0A0]/50"
    : "bg-white border-black/[0.10] focus:border-[#FF6B00] text-[#0A0A0A] placeholder-black/30";

  if (isLoading) return (
    <div className={`min-h-screen ${bg} flex items-center justify-center`}>
      <div className="w-8 h-8 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={`min-h-screen ${bg} flex flex-col items-center justify-center px-4 relative`}>
      <button onClick={toggleTheme} className={`absolute top-4 right-4 w-9 h-9 rounded-xl flex items-center justify-center border transition-all ${isDark ? "bg-white/[0.06] border-white/[0.10]" : "bg-black/[0.06] border-black/[0.08]"}`}>
        {isDark ? <Sun size={15} className="text-[#F5F5F5]" /> : <Moon size={15} className="text-[#0A0A0A]" />}
      </button>

      <Link to="/" className="flex items-center gap-3 mb-8">
        <div className="bg-[#FF6B00] px-2.5 py-1" style={{ boxShadow: isDark ? "0 0 16px rgba(255,107,0,0.40)" : "none" }}>
          <span className="font-display font-black text-white text-lg uppercase tracking-wider">PGF</span>
        </div>
        <span className={`font-display font-black text-xl uppercase tracking-widest ${text}`}>Pregame Fantasy</span>
      </Link>

      <div className={`w-full max-w-[440px] glass-card rounded-2xl p-7`}>
        <h1 className={`font-display font-black text-3xl uppercase mb-1 ${text}`}>Join League</h1>
        <p className={`font-body text-sm mb-6 ${muted}`}>Invite code: <span className="font-mono text-[#FF6B00]">{inviteCode}</span></p>

        {error && (
          <div className="bg-[#FF4444]/10 border border-[#FF4444]/40 rounded-xl px-4 py-3 flex items-center gap-2 mb-4">
            <AlertCircle size={15} className="text-[#FF4444]" />
            <p className="text-[#FF4444] font-body text-sm">{error}</p>
          </div>
        )}

        <div className="mb-4">
          <label className={`block font-body text-sm font-medium mb-1.5 ${muted}`}>Team Name (optional)</label>
          <input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder={`${user?.first_name}'s Team`}
            className={`w-full border rounded-xl px-4 py-3 font-body text-sm outline-none transition-colors ${inputCls}`} />
        </div>

        <button onClick={joinLeague} disabled={joining} data-testid="join-league-btn"
          className="w-full bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 text-white font-display font-black uppercase tracking-wide py-3 rounded-xl transition-all active:scale-[0.98]">
          {joining ? "Joining..." : "Join League"}
        </button>
      </div>
    </div>
  );
}
